import { useEffect, useRef, useState } from "react";
import api, { num, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, Truck, Paperclip, FileText, ImageIcon, X, Upload, Loader2, Boxes,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  forventet: { label: "Forventet", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  modtaget: { label: "Modtaget", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const imageUrl = (path) =>
  `${API_BASE}/dangoods/deliveries/image/${path}?auth=${encodeURIComponent(localStorage.getItem("cf_token") || "")}`;
const isPdf = (name = "") => name.toLowerCase().endsWith(".pdf");
const fmtDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${parseInt(d, 10)}/${parseInt(m, 10)}-${y}`;
};
const emptyForm = (sup) => ({
  date: todayIso(), supplier: sup || "DanGoods", pallets: "", description: "", status: "forventet",
  image_path: "", image_filename: "", content_type: "",
});

export function DangoodsDeliveries({ suppliers = [] }) {
  const supOptions = suppliers.length ? suppliers : ["DanGoods", "Mardin"];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(supOptions[0]));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/dangoods/deliveries"); setRows(r.data); }
    catch { toast.error("Kunne ikke hente leveringer"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm(supOptions[0])); setLocalPreview(""); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setLocalPreview("");
    setForm({
      date: d.date, supplier: d.supplier || supOptions[0], pallets: d.pallets ? String(d.pallets) : "",
      description: d.description, status: d.status,
      image_path: d.image_path || "", image_filename: d.image_filename || "", content_type: d.content_type || "",
    });
    setDialogOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okExt = /\.(jpe?g|png|webp|gif|heic|pdf)$/i.test(file.name);
    if (!okExt && !file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Kun billeder eller PDF er tilladt"); return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error("Filen er for stor (max 15 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/dangoods/deliveries/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: r.data.image_path, image_filename: r.data.image_filename, content_type: r.data.content_type || "" }));
      setLocalPreview(URL.createObjectURL(file));
      toast.success("Fil vedhæftet");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fejlede");
    } finally { setUploading(false); }
  };
  const removeAttachment = () => { setLocalPreview(""); setForm((f) => ({ ...f, image_path: "", image_filename: "", content_type: "" })); };

  const save = async () => {
    if (!form.date) { toast.error("Vælg en dato"); return; }
    const payload = { ...form, pallets: parseFloat(String(form.pallets).replace(",", ".")) || 0 };
    setSaving(true);
    try {
      if (editing) await api.put(`/dangoods/deliveries/${editing.id}`, payload);
      else await api.post("/dangoods/deliveries", payload);
      setDialogOpen(false);
      toast.success(editing ? "Levering opdateret" : "Levering oprettet");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke gemme");
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm("Slet denne levering?")) return;
    try { await api.delete(`/dangoods/deliveries/${d.id}`); toast.success("Levering slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const filtered = rows.filter((d) => statusFilter === "all" || d.status === statusFilter);
  const expected = rows.filter((d) => d.status === "forventet").length;
  const palletsExpected = rows.filter((d) => d.status === "forventet").reduce((s, d) => s + (d.pallets || 0), 0);

  return (
    <div className="max-w-4xl space-y-5" data-testid="dangoods-deliveries">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h4 className="font-heading text-xl font-light text-slate-900 flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#38BDF8]" /> Vareleveringer
          </h4>
          <p className="text-sm text-slate-500">Notér hvad der er på vej — antal paller m.v. {expected} forventet ({num(palletsExpected)} paller).</p>
        </div>
        <Button onClick={openNew} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2 self-start sm:self-auto" data-testid="dg-delivery-new-btn">
          <Plus className="w-4 h-4" /> Ny levering
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["all", "Alle"], ["forventet", "Forventet"], ["modtaget", "Modtaget"]].map(([id, label]) => (
          <button key={id} onClick={() => setStatusFilter(id)} data-testid={`dg-delivery-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === id ? "bg-[#38BDF8] text-[#0b1120]" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && <p className="text-sm text-slate-500 py-6" data-testid="dg-deliveries-loading">Henter leveringer…</p>}
        {!loading && filtered.map((d) => {
          const S = STATUS[d.status] || STATUS.forventet;
          return (
            <div key={d.id} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 flex flex-col sm:flex-row gap-4" data-testid={`dg-delivery-${d.id}`}>
              {d.image_path && (
                <button onClick={() => setPreview({ path: d.image_path, filename: d.image_filename })}
                  className="shrink-0 w-full sm:w-24 h-24 rounded-lg overflow-hidden bg-[#f8fafc] border border-[#e2e8f0] flex items-center justify-center hover:border-[#38BDF8] transition-colors"
                  data-testid={`dg-delivery-thumb-${d.id}`}>
                  {isPdf(d.image_filename)
                    ? <FileText className="w-8 h-8 text-slate-500" />
                    : <img src={imageUrl(d.image_path)} alt={d.image_filename} className="w-full h-full object-cover" />}
                </button>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${S.cls}`}>{S.label}</span>
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-500/15 text-sky-600 border border-sky-500/30">{d.supplier}</span>
                  <span className="text-xs text-slate-500">{fmtDate(d.date)}</span>
                </div>
                <p className="font-medium text-slate-800 mt-1.5 flex items-center gap-1.5">
                  <Boxes className="w-4 h-4 text-slate-500" /> {num(d.pallets)} paller
                </p>
                {d.description && <p className="text-sm text-slate-500 mt-0.5 whitespace-pre-wrap break-words">{d.description}</p>}
                {d.image_filename && (
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {d.image_filename}</p>
                )}
              </div>
              <div className="flex sm:flex-col gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => openEdit(d)}
                  className="h-8 w-8 text-slate-500 hover:text-sky-600" data-testid={`dg-delivery-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(d)}
                  className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`dg-delivery-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-12 text-center text-slate-500" data-testid="dg-deliveries-empty">
            Ingen leveringer endnu. Klik "Ny levering" for at notere hvad der kommer.
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-lg" data-testid="dg-delivery-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light">{editing ? "Redigér levering" : "Ny levering"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Dato</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="dg-delivery-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Leverandør</Label>
                <Select value={form.supplier} onValueChange={(v) => setForm((f) => ({ ...f, supplier: v }))}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="dg-delivery-supplier-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    {supOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Antal paller</Label>
                <Input type="number" min="0" step="1" value={form.pallets} onChange={(e) => setForm((f) => ({ ...f, pallets: e.target.value }))}
                  placeholder="fx 3" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="dg-delivery-pallets-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="dg-delivery-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="forventet">Forventet</SelectItem>
                    <SelectItem value="modtaget">Modtaget</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Beskrivelse (hvad kommer der?)</Label>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3} placeholder="Skriv varerne / paller / kolli…"
                className="mt-1 w-full rounded-md bg-[#f8fafc] border border-[#e2e8f0] text-slate-700 text-sm p-2.5 resize-y focus:outline-none focus:ring-1 focus:ring-[#38BDF8]"
                data-testid="dg-delivery-description-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Billede/PDF af ordren (valgfrit)</Label>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.heic" onChange={handleFile} className="hidden" data-testid="dg-delivery-file-input" />
              {form.image_path ? (
                <div className="mt-1 flex items-center gap-3 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-2.5">
                  {isPdf(form.image_filename)
                    ? <FileText className="w-9 h-9 text-slate-500 shrink-0" />
                    : <img src={localPreview || imageUrl(form.image_path)} alt="preview" className="w-12 h-12 rounded object-cover shrink-0" />}
                  <span className="flex-1 text-sm text-slate-500 truncate">{form.image_filename}</span>
                  <Button size="icon" variant="ghost" onClick={removeAttachment} className="h-8 w-8 text-slate-500 hover:text-red-600 shrink-0" data-testid="dg-delivery-remove-file-btn"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="mt-1 w-full rounded-lg bg-[#f8fafc] border-[#e2e8f0] border-dashed text-slate-500 hover:bg-[#eef2f6] hover:text-slate-900 gap-2 h-14" data-testid="dg-delivery-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploader…</> : <><Upload className="w-4 h-4" /> Vedhæft billede eller PDF</>}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-500 hover:text-slate-900" data-testid="dg-delivery-cancel-btn">Annullér</Button>
            <Button onClick={save} disabled={saving || uploading} className="rounded-lg bg-[#38BDF8] text-[#0b1120] hover:bg-sky-400 gap-2" data-testid="dg-delivery-save-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Gem" : "Opret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-3xl" data-testid="dg-delivery-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light flex items-center gap-2 text-base">
              {preview && isPdf(preview.filename) ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              {preview?.filename}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            isPdf(preview.filename)
              ? <iframe src={imageUrl(preview.path)} title={preview.filename} className="w-full h-[70vh] rounded-lg bg-white" />
              : <img src={imageUrl(preview.path)} alt={preview.filename} className="w-full max-h-[70vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
